/**
 * Code 128 (subset B) as SVG bars, for the bag labels and the register tags. The scanner at the
 * station and on the runner's phone reads AF-0001 back exactly as printed. Plain arithmetic,
 * no library: the 107 patterns, start B, the modulo-103 check digit, the stop pattern.
 */
const PATTERNS = [
  '212222',
  '222122',
  '222221',
  '121223',
  '121322',
  '131222',
  '122213',
  '122312',
  '132212',
  '221213',
  '221312',
  '231212',
  '112232',
  '122132',
  '122231',
  '113222',
  '123122',
  '123221',
  '223211',
  '221132',
  '221231',
  '213212',
  '223112',
  '312131',
  '311222',
  '321122',
  '321221',
  '312212',
  '322112',
  '322211',
  '212123',
  '212321',
  '232121',
  '111323',
  '131123',
  '131321',
  '112313',
  '132113',
  '132311',
  '211313',
  '231113',
  '231311',
  '112133',
  '112331',
  '132131',
  '113123',
  '113321',
  '133121',
  '313121',
  '211331',
  '231131',
  '213113',
  '213311',
  '213131',
  '311123',
  '311321',
  '331121',
  '312113',
  '312311',
  '332111',
  '314111',
  '221411',
  '431111',
  '111224',
  '111422',
  '121124',
  '121421',
  '141122',
  '141221',
  '112214',
  '112412',
  '122114',
  '122411',
  '142112',
  '142211',
  '241211',
  '221114',
  '413111',
  '241112',
  '134111',
  '111242',
  '121142',
  '121241',
  '114212',
  '124112',
  '124211',
  '411212',
  '421112',
  '421211',
  '212141',
  '214121',
  '412121',
  '111143',
  '111341',
  '131141',
  '114113',
  '114311',
  '411113',
  '411311',
  '113141',
  '114131',
  '311141',
  '411131',
  '211412',
  '211214',
  '211232',
  '2331112',
];
const START_B = 104;
const STOP = 106;

/** The bar widths for a Code 128B text, start to stop, as a run of module widths. */
export function code128Modules(text: string): number[] {
  const codes = [START_B];
  for (const ch of text) {
    const v = ch.charCodeAt(0) - 32;
    if (v < 0 || v > 94) throw new Error(`Code 128B cannot encode "${ch}"`);
    codes.push(v);
  }
  const check = codes.reduce((sum, c, i) => sum + c * (i === 0 ? 1 : i), 0) % 103;
  codes.push(check, STOP);
  const out: number[] = [];
  for (const c of codes) for (const w of PATTERNS[c] ?? '') out.push(Number(w));
  return out;
}

/** An SVG string: bars on white, `height` px tall, one module `unit` px wide, the text beneath. */
export function code128Svg(
  text: string,
  opts: { height?: number; unit?: number; quiet?: number; label?: boolean } = {},
): string {
  const height = opts.height ?? 48;
  const unit = opts.unit ?? 2;
  const quiet = opts.quiet ?? 10;
  const modules = code128Modules(text);
  const width = modules.reduce((n, w) => n + w, 0) * unit + quiet * 2 * unit;
  let x = quiet * unit;
  const bars: string[] = [];
  modules.forEach((w, i) => {
    if (i % 2 === 0)
      bars.push(
        `<rect x="${String(x)}" y="0" width="${String(w * unit)}" height="${String(height)}" fill="#000"/>`,
      );
    x += w * unit;
  });
  const label =
    opts.label === false
      ? ''
      : `<text x="${String(width / 2)}" y="${String(height + 14)}" text-anchor="middle" font-family="monospace" font-size="12">${text}</text>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${String(width)}" height="${String(height + (opts.label === false ? 0 : 18))}" viewBox="0 0 ${String(width)} ${String(height + (opts.label === false ? 0 : 18))}" role="img" aria-label="${text}"><rect width="100%" height="100%" fill="#fff"/>${bars.join('')}${label}</svg>`;
}
